import {
    Icon20CalendarOutline,
    Icon20HomeArrowDownOutline,
    Icon20MailOutline,
    Icon20MailStackOutline,
    Icon20ReplyOutline,
} from '@vkontakte/icons';
import app from 'Cloud/Application/app';
import domainFoldersModule from 'Cloud/Application/domainFolders';
import { stringify } from 'qs';
import React, { ReactElement } from 'react';
import { goTo } from 'reactApp/appHelpers/appHelpers';
import { IS_MY_TEAM, IS_ONPREMISE, IS_SOCIAL_USER } from 'reactApp/appHelpers/configHelpers';
import { UNMODIFIED_CLOUD_SPACE } from 'reactApp/constants/magicIdentificators';
import { attachFoldersRequest } from 'reactApp/modules/attaches/attaches.actions';
import { AttachesSelectors } from 'reactApp/modules/attaches/attaches.selectors';
import {
    getFeatureAlbumsPage,
    getFeatureAttachesEnabled,
    getFeatureDocuments,
    getFeaturePublicAutoDeletePromo,
} from 'reactApp/modules/features/features.selectors';
import { getDiscountGiftList } from 'reactApp/modules/giftReceived/giftReceived.selectors';
import { loadHomeFolderRequest } from 'reactApp/modules/home/home.actions';
import { getFolderListById, getHomeItemById } from 'reactApp/modules/home/home.selectors';
import { sortDiscountPromoBySpace } from 'reactApp/modules/promoTariffs/promoTariffs.selectors';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import {
    isDomainFolder,
    isMountedFolder,
    isPublicFolder,
    isReadOnlyMountedOrShared,
    isSharedFolder,
} from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { RootState, store } from 'reactApp/store';
import { ESelectFolderDialogMod } from 'reactApp/ui/SelectFolderDialog/SelectFolderDialog.types';
import { ETreeRootIds } from 'reactApp/ui/TreeComponent/TreeComponent.constants';
import { emptyArray } from 'reactApp/utils/helpers';
import { getHomeFolderPath } from 'reactApp/utils/urlHelper';
import { createSelector } from 'reselect';

import { TreeNodeData } from './TreeNode.types';

export const getCurrentTreeState = (state): { id: string; storage: EStorageType } => {
    const currentStorage = getCurrentStorage(state) as EStorageType;
    const currentRouteId = getCurrentRouteId(state);
    let currentId = '';

    if (currentStorage) {
        if (
            currentStorage === EStorageType.favorites ||
            currentStorage === EStorageType.trashbin ||
            currentStorage === EStorageType.sharedIncoming ||
            currentStorage === EStorageType.sharedLinks ||
            currentStorage === EStorageType.recommend ||
            currentStorage === EStorageType.start ||
            currentStorage === EStorageType.gallery ||
            currentStorage === EStorageType.sharedAutodelete ||
            currentStorage === EStorageType.feed ||
            currentStorage === EStorageType.documents ||
            currentStorage === EStorageType.albums
        ) {
            currentId = `/${currentStorage}`;
        } else if (currentStorage === EStorageType.attaches) {
            currentId = SettingsSelectors.getQueryParams(state)?.folderId || `/${EStorageType.attaches}`;
        } else {
            currentId = currentRouteId;
        }

        currentId = currentStorage + currentId;
    }

    return { id: currentId, storage: currentStorage };
};

const hideChildren = (item, dialogMod) =>
    dialogMod === ESelectFolderDialogMod.createShare && (isMountedFolder(item) || isSharedFolder(item) || isDomainFolder(item));

const getHomeFoldersTree = createSelector(
    (state: RootState): RootState => state,
    (state, dialogMod?: ESelectFolderDialogMod) => dialogMod,
    (state, dialogMod?: ESelectFolderDialogMod, skipRoFolders?: boolean) => skipRoFolders || false,
    (state, dialogMod?: ESelectFolderDialogMod, skipRoFolders?: boolean): TreeNodeData[] => {
        const mapFolderItemToTreeNodeData = (list) =>
            list
                ? list
                      .filter((item) => item?.isFolder)
                      .map((item) => {
                          const isRo = isReadOnlyMountedOrShared(state, item);

                          if (isRo && skipRoFolders) {
                              return;
                          }

                          return {
                              id: item.id,
                              href: item.href,
                              parent: item.parent,
                              isFolder: item.isFolder,
                              title: item.name,
                              storage: EStorageType.home,
                              folderCounter: !!item.count?.folders && !hideChildren(item, dialogMod) ? item.count?.folders : 0,
                              children: mapFolderItemToTreeNodeData(getFolderListById(state, item.id)),
                              isMountedOrShared: item.kind === 'mounted' || isDomainFolder(item) || item.kind === 'shared',
                              kind: item.kind,
                              isPublic: isPublicFolder(item),
                          };
                      })
                      .filter(Boolean)
                : emptyArray;

        return mapFolderItemToTreeNodeData(getFolderListById(state, '/'));
    }
);

const getAttachesFolderIcon = ({ type }: { type: string }): ReactElement | null => {
    switch (type) {
        case 'inbox':
            return <Icon20MailOutline />;
        case 'sent':
            return <Icon20ReplyOutline />;
        case 'newsletters':
            return <Icon20MailStackOutline />;
        case 'tomyself':
            return <Icon20HomeArrowDownOutline />;
        case 'user':
            return <Icon20CalendarOutline />;
        default:
            return null;
    }
};

const getAttachesFoldersTree = createSelector(
    (state: RootState): RootState => state,
    (state): TreeNodeData[] => {
        const mapAttachItemToTreeNodeData = (list) =>
            list
                ? list.map((item) => {
                      return {
                          id: item.id,
                          href: item.href,
                          title: item.text,
                          storage: EStorageType.attaches,
                          folderCounter: item.items ? item.items.length : 0,
                          children: mapAttachItemToTreeNodeData(item.items),
                          icon: getAttachesFolderIcon(item),
                      };
                  })
                : emptyArray;

        const folders = Array.from(Object.values(AttachesSelectors.getAttachesFoldersTree(state, '')));

        return mapAttachItemToTreeNodeData(folders);
    }
);

const getDomainFoldersTree = createSelector(
    (folders) => folders,
    (folders: any): TreeNodeData[] => {
        if (!folders) {
            return emptyArray;
        }

        return Array.from(Object.values(folders as { [key: string]: { id: string; name: string } })).map((item): TreeNodeData => {
            return {
                id: item.id,
                title: item.name,
                storage: EStorageType.home,
                folderCounter: 0,
                children: emptyArray,
                // принудительно ставим флаг, для корректного отображения иконок
                isMountedOrShared: true,
            };
        });
    }
);

export const getFoldersTree = createSelector(
    (state: RootState): RootState => state,
    (state, items: TreeNodeData[]): TreeNodeData[] => items,
    (state, items: TreeNodeData[], dialogMod?: ESelectFolderDialogMod) => dialogMod,
    (state, items: TreeNodeData[], dialogMod?: ESelectFolderDialogMod, skipRoFolders?: boolean) => skipRoFolders || false,
    // eslint-disable-next-line complexity
    (state, items: TreeNodeData[], dialogMod?: ESelectFolderDialogMod, skipRoFolders?: boolean): TreeNodeData[] => {
        const isBizUser = UserSelectors.isBizUser(state);

        items = [...items];

        let idx = items.findIndex((item) => item?.id === ETreeRootIds.home);
        if (idx >= 0) {
            items[idx] = {
                ...items[idx],
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                children: getHomeFoldersTree(state, dialogMod, skipRoFolders),
            };
            if (isBizUser) {
                items[idx].title = 'Личные папки';
                items[idx].children = items[idx].children?.filter((item) => !isDomainFolder(item) && item.kind !== 'mounted');
            }
        }

        idx = items.findIndex((item) => item?.id === ETreeRootIds.domain);
        if (idx >= 0) {
            if (isBizUser) {
                let children: TreeNodeData[] = [];
                const incomingFolders = getHomeFoldersTree(state).filter((item) => item.kind === 'mounted');

                /*
                    в окне сохранения в облако getDomainFoldersTree(domainFoldersModule.getFolders()) выдает пустой массив,
                    поэтому для этого окна доменные папки берем из home
                */
                if (dialogMod) {
                    children = getHomeFoldersTree(state).filter(isDomainFolder).concat(incomingFolders);
                } else {
                    children = getDomainFoldersTree(domainFoldersModule.getFolders()).concat(incomingFolders);
                }

                if (children && children.length > 0) {
                    items[idx] = {
                        ...items[idx],
                        children,
                    };
                }
            } else {
                items.splice(idx, 1);
            }
        }

        idx = items.findIndex((item) => item?.id === ETreeRootIds.attaches);
        if (idx >= 0) {
            if (getFeatureAttachesEnabled(state) && !IS_SOCIAL_USER) {
                items[idx] = {
                    ...items[idx],
                    children: getAttachesFoldersTree(state),
                };
            } else {
                items = items.filter((item) => item?.id !== ETreeRootIds.attaches);
            }
        }

        idx = items.findIndex((item) => item?.id === ETreeRootIds.recommend);
        if (idx >= 0 && IS_ONPREMISE) {
            items = items.filter((item) => item?.id !== ETreeRootIds.recommend);
        }

        idx = items.findIndex((item) => item?.id === ETreeRootIds.start);

        if (idx >= 0 && !!items[idx].children && !getFeaturePublicAutoDeletePromo(state)) {
            items[idx].children = items[idx]?.children?.filter((item) => item?.id !== ETreeRootIds.sharedAutodelete);
        }

        if (idx >= 0 && !!items[idx].children && !getFeatureDocuments(state)) {
            items[idx].children = items[idx]?.children?.filter((item) => item?.id !== ETreeRootIds.documents);
        }

        if (idx >= 0 && !!items[idx].children && !getFeatureAlbumsPage(state)) {
            items[idx].children = items[idx]?.children?.filter((item) => item?.id !== ETreeRootIds.albums);
        }

        idx = items.findIndex((item) => item?.id === ETreeRootIds.promocodes);

        if (idx >= 0 && !sortDiscountPromoBySpace(state).length && !getDiscountGiftList(state).length) {
            items = items.filter((item) => item?.id !== ETreeRootIds.promocodes);
        }

        if (IS_MY_TEAM) {
            items = items.filter((item) => item.id !== ETreeRootIds.recommend);
        }

        return items;
    }
);

export const loadFolder = async ({ id, storage, dispatch }): Promise<void> => {
    if (storage === EStorageType.attaches) {
        dispatch(attachFoldersRequest());
        return;
    }

    if (id === ETreeRootIds.domain || id === ETreeRootIds.promocodes) {
        return;
    }

    if (storage === EStorageType.home) {
        dispatch(loadHomeFolderRequest({ id, isFolder: true }));
    }
};

export const handleGoTo = ({ id, storage, href }) => {
    if (id === ETreeRootIds.domain) {
        return;
    }

    const { bytesUsed } = app.user.getSpace();

    if (storage === EStorageType.start) {
        if (app.isFeature('start-page') && bytesUsed !== UNMODIFIED_CLOUD_SPACE && bytesUsed !== 0 && !IS_ONPREMISE) {
            window.location.assign('/start');
        }
        return;
    }

    if (
        [
            EStorageType.sharedAutodelete,
            EStorageType.gallery,
            EStorageType.documents,
            EStorageType.albums,
            EStorageType.favorites,
            EStorageType.trashbin,
            EStorageType.feed,
            EStorageType.alldocuments,
            EStorageType.recommend,
        ].includes(storage)
    ) {
        window.location.assign(`/${storage}`);
        return;
    }

    if (storage === EStorageType.home) {
        const state = store.getState();
        const item = getHomeItemById(state, id) || app.get(id, 'home');

        window.location.assign(getHomeFolderPath(item.home));
        return;
    }

    if (storage === EStorageType.sharedIncoming || storage === EStorageType.sharedLinks) {
        goTo({
            storage,
        });
        return;
    }

    if (storage === EStorageType.attaches) {
        const { ...params } = app.getState().params;

        const query: Record<string, string> = { ...params };

        if (id && id !== '/attaches') {
            query.folderId = id;
        }

        window.location.assign(`/${storage}?${stringify(query)}`);
        return;
    }

    if (id) {
        goTo({ id });
    } else if (href) {
        app.goToLocation(href);
    }
};

export const getMoveText = (isItemAllowedToDrop: boolean, storage: EStorageType): string => {
    if (storage === EStorageType.trashbin) {
        return 'Удалить';
    }

    if (!isItemAllowedToDrop) {
        return 'Перемещение невозможно';
    }

    return 'Переместить';
};

/**
 * Сторы доступные для открытия бокового меню по прямому пути
 */
const AVAILABLE_TO_FORCE_OPEN_STORAGE_FROM_START_AND_INCOMING = [
    EStorageType.sharedLinks,
    EStorageType.sharedIncoming,
    EStorageType.feed,
    EStorageType.favorites,
    EStorageType.gallery,
    EStorageType.albums,
    EStorageType.sharedAutodelete,
    EStorageType.documents,
];

export const checkForceOpenFoldersOnThePath = (
    id: string,
    selectedStorage: EStorageType,
    selectedId: string,
    children?: TreeNodeData[]
) => {
    const { pathname, search } = window?.location || {};
    const _selectedId = selectedId.match(/(\d+)/)?.[0];

    /**
     * Раскрываем аттачи (Из почты) в случае присутствия search в URI.
     * @example /attaches/?folderId=0
     */
    if (selectedStorage === EStorageType.attaches && search && selectedId.includes(EStorageType.attaches) && _selectedId) {
        return children?.some((c) => c.id === _selectedId) || children?.some((c) => c.children?.some((cc) => cc.id === _selectedId));
    }
    /**
     * Раскрываем аттачи (Из почты) только в случае отсутсвия search в URI.
     * @example /attaches
     */
    if (selectedStorage === EStorageType.attaches && !search && id === ETreeRootIds.attaches) {
        return true;
    }

    if (
        /**
         * Раскрываем Хомяк (Все файлы) только в случае содержании в pathname минимум одной 1 папку после /home.
         * @example /home/folder
         */
        selectedStorage === EStorageType.home &&
        pathname?.split('/').filter(Boolean).length > 1
    ) {
        return decodeURIComponent(`${pathname}${search}`).includes(id);
    }

    /**
     * Раскрываем стартовую страницу (Быстрый доступ или /start) только для определенных хранилищ.
     * Частный случай это путь /incoming он нужен для корректной работы shared/incoming и shared/links
     */
    return (
        (id === ETreeRootIds.start || id === ETreeRootIds.incoming) &&
        AVAILABLE_TO_FORCE_OPEN_STORAGE_FROM_START_AND_INCOMING.includes(selectedStorage)
    );
};
